/* eslint-disable import/no-duplicates */
import axios, { AxiosError } from 'axios'
import { getUserSessionStorage } from '@/composables/useSessions'
import useSessions from '@/composables/useSessions'

import { setSnackbar, closeSnackbar } from '@/composables/useSnackbar'
import { bookingSecretFor, guestBookingRefIn } from '@/composables/useBookingSecret'
import router from '@/router'

const httpClient = axios.create({
  baseURL: `${process.env.VUE_APP_EXPRESSWAY_URL}/private`,
  timeout: 16000,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': process.env.VUE_APP_EXPRESSWAY_API_KEY
  }
})

const httpClientBasic = axios.create({
  baseURL: `${process.env.VUE_APP_EXPRESSWAY_URL}/private`,
  timeout: 16000,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': process.env.VUE_APP_EXPRESSWAY_API_KEY
  }
})

const parseErrorMessage = (error: AxiosError) => {
  const message = error.response?.data?.Message
  try {
    if (typeof message === 'string') {
      return message
    } else if (Array.isArray(message)) {
      // Check if any item in the array has both Messages and Errors properties
      const hasMessagesAndErrors = message.some(item =>
        item && typeof item === 'object' && 'Messages' in item && 'Errors' in item
      )

      if (hasMessagesAndErrors) {
        // Return the original message structure as is
        return message
      } else {
        // Use the original parsing logic for other array structures
        return message.map(obj =>
          Object.values(obj)[0]
        ).join(', ')
      }
    } else {
      return error.message
    }
  } catch (e) {
    return message
  }
}

httpClient.interceptors.request.use(
  config => {
    const userSession = getUserSessionStorage()
    if (userSession?.AccessToken) {
      config.headers.Authorization = `Bearer ${userSession.AccessToken}`
    } else {
      delete config.headers.Authorization
    }

    // A message from an earlier request must not sit on screen while a new one
    // is in flight, or it reads as though the new attempt just failed. Only for
    // mutations: background GETs (swrv revalidation, realtime polling) would
    // otherwise wipe a message the visitor has not read yet.
    if (config.method?.toLowerCase() !== 'get') closeSnackbar()

    // A guest booking is only readable by whoever did the lookup, proven with
    // the secret it was issued. Kept out of the URL on purpose, so it is sent
    // here rather than being part of any shareable link.
    const guestBookingRef = guestBookingRefIn(config.url, config.data)
    if (guestBookingRef) {
      const secret = bookingSecretFor(guestBookingRef)
      if (secret) config.headers['X-Booking-Secret'] = secret
    }

    return config
  },
  error => {
    Promise.reject(error)
  }
)

httpClient.interceptors.response.use(
  response =>
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    response
  ,
  (error: AxiosError) => {
    if (error?.response?.status === 401) {
      const userSession = getUserSessionStorage()
      if (userSession) {
        const { logOut } = useSessions()
        logOut(true)
      }
      const currentPath = router.currentRoute.value.fullPath
      const loginRoute: { name: string, query?: { redirect: string } } = { name: 'Login' }
      if (currentPath && currentPath !== '/' && !currentPath.startsWith('/login')) {
        loginRoute.query = { redirect: currentPath }
      }
      router.push(loginRoute).then(() =>
        setSnackbar('Access denied. You must sign in to continue.')
      )
    }
    error.message = parseErrorMessage(error)
    return Promise.reject(error)
  }
)

export { httpClient, httpClientBasic }
